import {OrdersCartHintItem, OrdersCartHintItemStock} from "../../../../../models/orders/orders-cart/orders-create/orders.cart";

export interface NormalizedHintStock {
    warehouse: string;
    requiredQty: number;
    availableQty: number;
    replacementQtyFactor: number;
    type?: OrdersCartHintItemStock["type"];
    fullyQtyReplaceable: boolean;
}

export const getHintItemStocks = (item: OrdersCartHintItem): NormalizedHintStock[] => {
    const stocks = Array.isArray(item.stock) ? item.stock : item.stocks;
    if (!Array.isArray(stocks)) return [];
    return stocks.flatMap(stock => {
        const requiredQty = Number.isFinite(stock.requiredQty) ? stock.requiredQty! : item.requestedQty;
        const availableQty = Number.isFinite(stock.availableQty) ? stock.availableQty! : stock.stock;
        const replacementQtyFactor = Number.isFinite(stock.replacementQtyFactor) ? stock.replacementQtyFactor! : 1;
        if (typeof stock.warehouse !== "string" || !Number.isFinite(requiredQty) || requiredQty <= 0
            || !Number.isFinite(availableQty) || availableQty! < 0 || replacementQtyFactor <= 0) return [];
        return [{
            warehouse: stock.warehouse, requiredQty, availableQty: availableQty!, replacementQtyFactor,
            type: stock.type, fullyQtyReplaceable: stock.fullyQtyReplaceable ?? availableQty! >= requiredQty,
        }];
    });
};

export const getVisibleHintStocks = (stocks: NormalizedHintStock[], warehouses: string[]) =>
    stocks.filter(stock => warehouses.includes(stock.warehouse));

export const getHintCoverage = (stocks: NormalizedHintStock[], warehouses: string[], useBackorder = false) => {
    const visibleStocks = getVisibleHintStocks(stocks, warehouses);
    const replacementStock = visibleStocks[0] || stocks[0];
    const requiredQty = replacementStock?.requiredQty || 0;
    const availableQty = visibleStocks.reduce((total, stock) => total + stock.availableQty, 0);
    return {
        requiredQty, availableQty, replacementQtyFactor: replacementStock?.replacementQtyFactor,
        type: replacementStock?.type, hasStock: useBackorder || Boolean(replacementStock),
        isFullyReplaceable: visibleStocks.some(stock => stock.fullyQtyReplaceable) && availableQty >= requiredQty,
        qtyToAdd: useBackorder ? requiredQty : Math.min(requiredQty, availableQty),
    };
};

export const formatHintQty = (value: number) => value.toLocaleString();
export const createHintRow = (item: OrdersCartHintItem, warehouses: string[], useBackorder = false) => {
    const stocks = getHintItemStocks(item);
    return {
        key: JSON.stringify([item.requestedNo, item.article.id]), item,
        stocks: getVisibleHintStocks(stocks, warehouses), coverage: getHintCoverage(stocks, warehouses, useBackorder),
    };
};
export type HintTableRow = ReturnType<typeof createHintRow>;
